import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import Navbar from "../components/Navbar";
import { LogoMark } from "../components/Logo";

function Content({ body }) {
  return (
    <div className="mt-10 space-y-5">
      {body.split("\n\n").map((block, i) => {
        const t = block.trim();
        if (t.startsWith("## ")) {
          return (
            <h2 key={i} className="pt-6 font-display text-2xl font-extrabold uppercase text-white">
              {t.slice(3)}
            </h2>
          );
        }
        return (
          <p key={i} className="text-slate-300 leading-relaxed text-base">
            {t}
          </p>
        );
      })}
    </div>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api(`/articles/slug/${slug}`)
      .then(setArticle)
      .catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div className="min-h-screen bg-ink text-white overflow-x-clip">
      {article && <Seo title={`${article.title} | Fitness Fanatic Nik`} description={article.excerpt} />}
      <div className="grain" aria-hidden="true" />
      <Navbar crossPage />
      <main className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-40 pb-24">
        {notFound ? (
          <div className="text-center py-24" data-testid="article-not-found">
            <p className="font-display text-3xl font-extrabold uppercase text-white">Article not found</p>
            <Link to="/blog" data-testid="article-back-to-blog" className="mt-6 inline-flex items-center gap-2 text-emerald-400 font-semibold">
              <ArrowLeft className="h-4 w-4" /> Back to all articles
            </Link>
          </div>
        ) : !article ? (
          <div className="flex justify-center py-32">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        ) : (
          <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="article-detail">
            <Link to="/blog" data-testid="article-breadcrumb" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition-colors">
              <ArrowLeft className="h-4 w-4" /> All Articles
            </Link>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="px-3 py-1 rounded-full bg-gold/10 border border-gold/30 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
                {article.category}
              </span>
              <span className="font-mono text-xs text-slate-500">
                {article.created_at ? new Date(article.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : ""}
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
              {article.title}
            </h1>
            <p className="mt-5 text-lg text-slate-400 leading-relaxed">{article.excerpt}</p>
            <Content body={article.content} />
            <div className="mt-16 rounded-3xl border border-accent/30 bg-gradient-to-br from-panel to-ink p-10" data-testid="article-cta-card">
              <div className="flex items-center gap-3">
                <LogoMark className="h-10 w-10" />
                <p className="font-display text-xl font-extrabold uppercase text-white">Want a plan built for YOU?</p>
              </div>
              <p className="mt-3 text-sm text-slate-400">
                General guides are a start — a custom protocol gets results. Apply for 1-on-1 coaching with Nikhil.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/#intake-form"
                  data-testid="article-cta-apply"
                  className="px-6 py-3 rounded-full bg-gradient-to-br from-accent to-accent-dim text-ink font-extrabold uppercase tracking-wide text-sm transition-all hover:shadow-glow-emerald"
                >
                  Apply For Coaching
                </Link>
                <a
                  href="https://wa.me/917709589870"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="article-cta-whatsapp"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-accent/50 text-emerald-300 font-bold uppercase tracking-wide text-sm transition-all hover:bg-accent/10"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>
          </motion.article>
        )}
      </main>
    </div>
  );
}